import { ApiError, api, type WalletNoncePurpose } from '@alpha/api-client';
import { useMutation } from '@tanstack/react-query';
import { ExternalLink, QrCode, Wallet } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  connectWallet,
  connectWalletConnect,
  disconnectWalletConnect,
  ensureBscNetwork,
  hasInjectedProvider,
  metaMaskDeepLink,
  shortAddress,
  signMessage,
  trustWalletDeepLink,
  WalletConnectError,
} from '../lib/wallet';
import { Alert, Button, Card } from '../ui';

/**
 * Wallet-connect login — the 2026-07-30 replacement for the password login
 * form. There is NO password field anywhere on this page; existing members
 * authenticate purely by connecting their registered wallet and signing a
 * single-use nonce challenge (SIWE-style). See
 * App\Http\Controllers\Auth\WalletAuthController::login for the backend side.
 */
export function Login() {
  const navigate = useNavigate();
  const [address, setAddress] = useState<string | null>(null);
  const [step, setStep] = useState<'idle' | 'connecting' | 'signing' | 'verifying'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [hasProvider, setHasProvider] = useState(() => hasInjectedProvider());

  // Some wallet in-app browsers inject `window.ethereum` a beat after the
  // page loads rather than synchronously. Re-check once so the deep-link
  // fallback below doesn't linger for a mobile wallet browser that just
  // needed a moment to announce itself.
  useEffect(() => {
    if (hasProvider) return;
    const recheck = () => setHasProvider(hasInjectedProvider());
    window.addEventListener('ethereum#initialized', recheck, { once: true });
    const timer = window.setTimeout(recheck, 500);
    return () => {
      window.removeEventListener('ethereum#initialized', recheck);
      window.clearTimeout(timer);
    };
  }, [hasProvider]);

  // WalletConnect sessions hold a live relay socket open — dispose it if the
  // member navigates away without completing sign-in, so it doesn't linger.
  useEffect(() => {
    return () => {
      disconnectWalletConnect().catch(() => {});
    };
  }, []);

  const requestSignature = async (purpose: WalletNoncePurpose, wallet: string) => {
    const { data } = await api.walletAuth.nonce({ wallet_address: wallet, purpose });
    setStep('signing');
    const signature = await signMessage(wallet, data.message);
    return { nonce: data.nonce, signature };
  };

  const signIn = useMutation({
    mutationFn: async (method: 'injected' | 'walletconnect') => {
      setError(null);
      setStep('connecting');
      await ensureBscNetwork();
      const wallet = method === 'walletconnect' ? await connectWalletConnect() : await connectWallet();
      setAddress(wallet);

      setStep('verifying');
      const { nonce, signature } = await requestSignature('login', wallet);
      return api.walletAuth.login({ wallet_address: wallet, nonce, signature });
    },
    onSuccess: (result) => {
      setStep('idle');
      navigate(result.requires_two_factor ? '/two-factor' : '/', { replace: true });
    },
    onError: (err) => {
      setStep('idle');
      if (err instanceof WalletConnectError || err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Something went wrong while signing in. Please try again.');
      }
    },
  });

  const busy = signIn.isPending;
  const busyLabel = {
    idle: null,
    connecting: 'Connecting wallet…',
    signing: 'Waiting for signature…',
    verifying: 'Verifying…',
  }[step];
  const activeMethod = signIn.variables;
  const injectedLabel = busy && activeMethod === 'injected' ? busyLabel : 'Connect Wallet & Sign In';
  const walletConnectLabel =
    busy && activeMethod === 'walletconnect' ? busyLabel : 'Connect with WalletConnect (any wallet)';

  return (
    <div className="auth-wrap">
      <Card className="auth-card">
        <h1 className="auth-title">Alpha One</h1>
        <p className="auth-subtitle">Sign in with your wallet — no password required.</p>

        {error ? <Alert tone="danger">{error}</Alert> : null}

        {!hasProvider ? (
          <div style={{ marginBottom: 16 }}>
            <Alert tone="warning">No wallet app detected in this browser.</Alert>
            <p className="text-muted small" style={{ marginBottom: 8 }}>
              Continue in your wallet app&apos;s built-in browser instead:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Button
                type="button"
                variant="outline-gold"
                className="w-100"
                rightIcon={<ExternalLink size={14} />}
                onClick={() => {
                  window.location.href = metaMaskDeepLink(window.location.href);
                }}
              >
                Open in MetaMask
              </Button>
              <Button
                type="button"
                variant="outline-gold"
                className="w-100"
                rightIcon={<ExternalLink size={14} />}
                onClick={() => {
                  window.location.href = trustWalletDeepLink(window.location.href);
                }}
              >
                Open in Trust Wallet
              </Button>
            </div>
          </div>
        ) : null}

        {address ? (
          <p className="text-muted small" style={{ marginBottom: 16 }}>
            Connected: <strong>{shortAddress(address)}</strong>
          </p>
        ) : null}

        <Button
          type="button"
          variant="gold"
          className="w-100"
          disabled={busy}
          leftIcon={<QrCode size={16} />}
          onClick={() => signIn.mutate('walletconnect')}
          style={{ marginBottom: 8 }}
        >
          {walletConnectLabel}
        </Button>

        <Button
          type="button"
          variant="outline-gold"
          className="w-100"
          disabled={busy}
          leftIcon={<Wallet size={16} />}
          onClick={() => signIn.mutate('injected')}
        >
          {injectedLabel}
        </Button>

        <p className="auth-footer">
          Don&apos;t have an account? <Link to="/register">Register with your wallet</Link>
        </p>

        <p className="text-muted small" style={{ marginTop: 16 }}>
          No extension or wallet app? Use <strong>Connect with WalletConnect</strong> to scan a QR code with any
          wallet app on your phone. Already have the MetaMask extension, or opened this page inside a wallet
          app&apos;s built-in browser? Use <strong>Connect Wallet</strong> instead.
        </p>
      </Card>
    </div>
  );
}
